import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import _ from 'lodash';

import { User } from '../../../models/Core/User';

const TIMEOUT_UPDATE_SORT_ORDER = 10000;

class SelectableUserDecorator {
  user: User;
  selected: boolean;

  constructor(user) {
    this.user = user;
    this.selected = false;
  }
}

@Component({
  selector: 'app-user-filter',
  templateUrl: './user-filter.component.html',
  styleUrls: ['./user-filter.component.scss'],
})
export class UserFilterComponent implements OnInit {
  public selectedUsers = {};
  private sortedUsers: User[];
  private isFiltered = {};
  private componentTriggeredFilterUpdate = false;

  @Input() set filteredUsers(users: User[]) {
    this.setFilteredUsers(users);
    this.initializeDropdownList();

    if (this.getUsers()) {
      this.updateFilteredUserMapping();
      if (this.timeoutHandle) {
        clearTimeout(this.timeoutHandle);
      }

      if (this.componentTriggeredFilterUpdate) {
        // delay update
        this.timeoutHandle = setTimeout(
          () => this.updateSortedUsers(),
          TIMEOUT_UPDATE_SORT_ORDER,
        );
        this.componentTriggeredFilterUpdate = false;
      } else {
        this.updateSortedUsers();
      }
    }
  }

  @Output() updatedFilter = new EventEmitter();

  @Input() set users(users: User[]) {
    this.setUsers(users);
    this.selectedUsers = {};
    for (const user of users) {
      this.selectedUsers[user._id] = false;
    }

    this.initializeDropdownList();

    if (this.getFilteredUsers()) {
      this.updateFilteredUserMapping();
      this.updateSortedUsers();
    }
  }

  @Output() userSelectedEvent = new EventEmitter<User[]>();
  @Output() userHoverEvent = new EventEmitter<User>();

  @ViewChild(VirtualScrollerComponent, { static: true })
  scroll: VirtualScrollerComponent;

  @ViewChild('divParent', { static: true }) divParent: ElementRef;

  private timeoutHandle: any = null;
  private _filteredUsers: User[] = [];

  selectableUsers: SelectableUserDecorator[] = [];
  height = '400px';
  width = '400px';
  private _users: User[] = [];

  // call this function after resize + animation end
  dropdownList = [];
  selectedUserList = [];

  initializeDropdownList() {
    this.dropdownList = this.getUsers()
      .filter(user => this.isUserFiltered(user) || this.isUserSelected(user))
      .map(user => {
        return {
          id: user._id,
          name: `${user.first_name || ''} ${user.name || ''} ${user.organization ? '(' + user.organization + ')' : ''}`,
        };
      });
  }

  get placeHolderString() {
    if (this.getUsers().length > this.getFilteredUsers().length) {
      return `Select or Search Users (${this.getFilteredUsers().length} of ${this.getUsers().length})`;
    } else {
      return `Select or Search Users (${this.getUsers().length})`;
    }
  }

  isFilteredUser(user: User) {
    return this.isFiltered[user._id];
  }

  setUsers(users: User[]) {
    this._users = users;
  }

  getSortedUsers(): User[] {
    return this.sortedUsers;
  }

  updateSortedUsers() {
    this.sortedUsers = _.sortBy(this.getUsers(), user => {
      if (this.selectedUsers[user._id] && this.isFilteredUser(user)) {
        return 0;
      } else if (this.selectedUsers[user._id]) {
        return 1;
      } else if (this.isFilteredUser(user)) {
        return 2;
      } else {
        return 3;
      }
    });
  }

  getUsers(): User[] {
    return this._users;
  }

  setFilteredUsers(users: User[]) {
    this._filteredUsers = users;
  }

  getFilteredUsers() {
    return this._filteredUsers;
  }

  @HostListener('window:resize', ['$event'])
  afterResize() {
    this.height = this.divParent.nativeElement.offsetHeight + 'px';
    this.width = this.divParent.nativeElement.offsetWidth + 'px';
    this.scroll.refresh();
  }

  isUserSelected(user) {
    return this.selectedUsers[user._id];
  }

  isUserFiltered(user) {
    return (
      this.getFilteredUsers()
        .map(filteredUser => filteredUser._id)
        .indexOf(user._id) >= 0
    );
  }

  toggleUser(user) {
    this.selectedUsers[user._id] = !this.selectedUsers[user._id];
    this.selectedUserList = [];
    for (const userId of _.keys(this.selectedUsers)) {
      if (this.selectedUsers[userId]) {
        this.selectedUserList.push(userId);
      }
    }

    this.updateFilter();
  }

  ngOnInit() {
    this.height = this.divParent.nativeElement.offsetHeight + 'px';
    this.width = this.divParent.nativeElement.offsetWidth + 'px';
  }

  onSelectionChanged(selectedUserIds) {
    for (const userId of _.keys(this.selectedUsers)) {
      this.selectedUsers[userId] = false;
    }

    for (const userId of selectedUserIds) {
      this.selectedUsers[userId] = true;
    }

    this.updateFilter();
  }

  updateFilter() {
    this.componentTriggeredFilterUpdate = true;
    const selectedUserIds = _.keys(this.selectedUsers).filter(
      userId => this.selectedUsers[userId],
    );
    this.updatedFilter.emit(selectedUserIds);
  }

  updateFilteredUserMapping() {
    for (const user of this.getUsers()) {
      this.isFiltered[user._id] = false;
    }
    for (const user of this.getFilteredUsers()) {
      this.isFiltered[user._id] = true;
    }
  }

  clearUser(userId: any) {
    const selectedUser = _.find(this.getUsers(), user => user._id === userId);
    this.toggleUser(selectedUser);
  }
}
